import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import PurchaseDetails from "../components/ClientForm/PurchaseDetails";
import Applicants from "../components/ClientForm/Applicants";
import Income from "../components/ClientForm/Income";
import Liabilities from "../components/ClientForm/Liabilities";
import SummarySubmit from "../components/ClientForm/SummarySubmit";
import ProgressBar from "../components/ClientForm/ProgressBar";
import styles from "../components/ClientForm/ClientForm.module.css";

// Helper: read query params
function useQuery() {
  return new URLSearchParams(useLocation().search);
}

// Helper: pull some UTM params if present (nice for your Zap email)
function readUtm(q) {
  return {
    utm_source: q.get("utm_source") || "",
    utm_medium: q.get("utm_medium") || "",
    utm_campaign: q.get("utm_campaign") || "",
  };
}

function ClientApplication() {
  const [step, setStep] = useState(1);
  const query = useQuery();

  const [formData, setFormData] = useState({
    // Step 1
    isFirstHomeBuyer: "",
    purchasePrice: "",
    deposit: "",
    timeframe: "",
    suburb: "",

    // Step 2
    name: "",
    email: "",
    phone: "",
    maritalStatus: "",
    buyingWithSomeone: false,
    coName: "",
    coEmail: "",
    coPhone: "",

    // Step 3
    applicant1: { employmentType: "", income: "" },
    applicant2: { employmentType: "", income: "" },

    // Step 4
    liabilities: [],
  });

  // Prefill from query if present (optional convenience)
  useEffect(() => {
    const email = query.get("email");
    const name = query.get("name");
    const phone = query.get("phone");

    if (email || name || phone) {
      setFormData((prev) => ({
        ...prev,
        name: name || prev.name,
        email: email || prev.email,
        phone: phone || prev.phone,
      }));
    }
  }, [query]);

  // 🔔 ALWAYS trigger a “form started” alert to Zapier on first load this session
  useEffect(() => {
    // prevent duplicate pings from strict mode/hmr; still fires on each fresh visit
    if (sessionStorage.getItem("mm_form_start_ping_sent")) return;

    const { utm_source, utm_medium, utm_campaign } = readUtm(query);

    const payload = {
      event: "ApplicationStarted",
      // Send whatever we have (blank allowed)
      name: formData.name || "",
      email: formData.email || "",
      phone: formData.phone || "",
      // Useful metadata
      utm_source,
      utm_medium,
      utm_campaign,
      path: window.location.pathname + window.location.search,
      referrer: document.referrer || "",
      userAgent: navigator.userAgent,
      adminNotify: "admin@mortgagematrix.com.au",
      timestamp: new Date().toISOString(),
    };

    // Fire-and-forget; `no-cors` avoids browser blocks
    fetch("https://hooks.zapier.com/hooks/catch/17916584/umu4jrm/", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      mode: "no-cors",
    })
      .then(() => {
        console.log("✅ Zapier start trigger sent");
        sessionStorage.setItem("mm_form_start_ping_sent", "1");
      })
      .catch((err) => {
        // Even if it fails, do not block the user flow
        console.error("❌ Zapier start trigger error:", err);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // run once per visit

  const nextStep = () => setStep((prev) => prev + 1);
  const prevStep = () => setStep((prev) => prev - 1);

  return (
    <div className={styles.container}>
      <ProgressBar step={step} totalSteps={5} />

      {step === 1 && (
        <PurchaseDetails
          formData={formData}
          setFormData={setFormData}
          nextStep={nextStep}
        />
      )}

      {step === 2 && (
        <Applicants
          formData={formData}
          setFormData={setFormData}
          nextStep={nextStep}
          prevStep={prevStep}
        />
      )}

      {step === 3 && (
        <Income
          formData={formData}
          setFormData={setFormData}
          nextStep={nextStep}
          prevStep={prevStep}
        />
      )}

      {step === 4 && (
        <Liabilities
          formData={formData}
          setFormData={setFormData}
          nextStep={nextStep}
          prevStep={prevStep}
        />
      )}

      {step === 5 && <SummarySubmit formData={formData} prevStep={prevStep} />}
    </div>
  );
}

export default ClientApplication;
